import { useState, useCallback } from "react";
import { addTemplateSection } from "@/lib/api";
import { toast } from "@/hooks/use-toast";

export const usePrescriptionSections = () => {
  const [minimizedSections, setMinimizedSections] = useState<Record<string, boolean>>({
    complaints: false,
    medicines: false,
    investigations: false,
    advice: false,
    'next-visit': false,
    notes: false,
    vitals: false,
    diagnosis: false,
  });
  const [activeLoadType, setActiveLoadType] = useState<string | null>(null);
  const [activeSaveType, setActiveSaveType] = useState<string | null>(null);

  const handleMinimizeSection = useCallback((sectionId: string) => {
    setMinimizedSections(prev => ({ ...prev, [sectionId]: true }));
  }, []);

  const handleExpandSection = useCallback((sectionId: string) => {
    setMinimizedSections(prev => ({ ...prev, [sectionId]: false }));
  }, []);

  const handleLoadTemplate = useCallback((type: string) => {
    setActiveLoadType(type);
  }, []);

  const handleSaveTemplate = useCallback((type: string) => {
    setActiveSaveType(type);
  }, []);

  const closeLoadTemplate = useCallback(() => {
    setActiveLoadType(null);
  }, []);

  const closeSaveTemplate = useCallback(() => {
    setActiveSaveType(null);
  }, []);

  const handleClearSection = useCallback((sectionId: string, sectionLabel: string) => {
    const confirmed = window.confirm(`Are you sure you want to clear the ${sectionLabel} section?`);
    if (!confirmed) return;
    return { sectionId, confirmed: true };
  }, []);

  const saveTemplate = useCallback(async (templateData: {
    title: string;
    type: 'complaint' | 'medicine' | 'investigation' | 'advice' | 'diagnosis' | 'quick_notes';
    category: 'personal' | 'clinic';
    doctor_id: string;
    clinic_id: string;
    value?: any;
  }) => {
    try {
      await addTemplateSection({
        type: templateData.type,
        title: templateData.title,
        category: templateData.category,
        doctor_id: templateData.doctor_id,
        clinic_id: templateData.clinic_id,
        value: templateData.value,
      });

      toast({
        title: "Template Saved",
        description: `${templateData.title} has been saved`,
      });

      setActiveSaveType(null);
      return true;
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Error",
        description: "Failed to save template. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, []);

  const getSectionCount = useCallback((sectionId: string, formValues: any) => {
    switch (sectionId) {
      case 'complaints':
        return formValues.complaints?.filter((c: any) => typeof c.complaint === 'string' && c.complaint.trim()).length || 0;
      case 'medicines':
        return formValues.medicines?.filter((m: any) => (m.brand_name || m.product)?.trim()).length || 0;
      case 'investigations':
        return formValues.investigations?.filter((i: any) => typeof i === 'string' && i.trim()).length || 0;
      default:
        return null;
    }
  }, []);

  const getSectionStatus = useCallback((sectionId: string, formValues: any) => {
    switch (sectionId) {
      case 'complaints':
        return formValues.complaints?.some((c: any) => typeof c.complaint === 'string' && c.complaint.trim());
      case 'vitals':
        return Object.values(formValues.vitals || {}).some(v => v && v !== 'None');
      case 'diagnosis':
        return typeof formValues.diagnosis === 'string' && formValues.diagnosis.trim();
      case 'medicines':
        return formValues.medicines?.some((m: any) => (m.brand_name || m.product)?.trim());
      case 'investigations':
        return formValues.investigations?.some((i: any) => typeof i === 'string' && i.trim());
      case 'advice':
        return typeof formValues.advice === 'string' && formValues.advice.trim();
      case 'notes':
        return typeof formValues.quick_notes === 'string' && formValues.quick_notes.trim();
      case 'next-visit':
        return formValues.next_visit?.date || formValues.next_visit?.time || formValues.next_visit?.notes;
      default:
        if (sectionId.startsWith('custom_')) {
          const value = formValues.custom_fields?.[sectionId];
          return typeof value === 'string' && !!value.trim();
        }
        return false;
    }
  }, []);

  return {
    minimizedSections,
    activeLoadType,
    activeSaveType,
    handleMinimizeSection,
    handleExpandSection,
    handleClearSection,
    handleLoadTemplate,
    handleSaveTemplate,
    closeLoadTemplate,
    closeSaveTemplate,
    saveTemplate,
    getSectionCount,
    getSectionStatus,
  };
};